import BaseModel from './BaseModel'
import {action, makeObservable, observable} from 'mobx'
import {post} from '../common/requester'

import BillingGroup from './BillingGroup'
import BillingGroupQuery from './BillingGroupQuery'


const formatDate = (date) => {
    if (!date) return null

    if (typeof date === 'string') {
        return date
    }

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}


export default class Billing extends BaseModel {
    constructor(store) {
        super(store)

        makeObservable(this, {
            query: observable,

            items: observable,
            group: observable,
            group_billings: observable,
            currency: observable,
            objects: observable,
            periods: observable,
            members: observable,

            top_debts: observable,

            search: action,
            getGroup: action,
            resetGroup: action,

            getObjects: action,
            getTrainingPeriods: action,
            getMembers: action,
            resetCreateOptions: action,

            addGroup: action,
            updateGroup: action,
            addMembers: action,

            getTopDebts: action,
            deleteBilling: action,
            deleteCheque: action,

            generateGameBilling: action,
            informGameBilling: action,
            memberBillings: observable,
            getMemberBillings: action,
        })
    }

    query = new BillingGroupQuery()

    items = []

    group = new BillingGroup()
    group_billings = []
    currency = {}
    objects = []
    periods = []
    members = []

    top_debts = []

    memberBillings = {
        company_user: null,
        items: [],
        total: 0,
        page_number: 1,
        page_size: 30,
        page_count: 0,
        loading: false
    };
    get company_id() {
        return this.query.company_id
    }


    async search() {
        const query = this.query.toJson()

        query.date_from = formatDate(this.query.date_from)
        query.date_to = formatDate(this.query.date_to)

        const res = await post(
            'manager/billing/groups',
            {
                query
            }
        ).catch((err) => {
            console.error('--billing groups error', err)
        })

        if (!res || res.result !== 0) {
            return
        }

        this.query.fromJson({
            page_number: res.page_number,
            page_count: res.page_count,
            page_size: res.page_size,
            total: res.total
        })

        const items = (res.items || []).map((item) => {
            const group = new BillingGroup()
            group.fromJson(item)
            return group
        })

        this.setProperty('items', items)
    }


    async getGroup(billing_group_id) {
        const res = await post(
            'manager/billing/group',
            {
                company_id: this.company_id,
                billing_group_id
            }
        ).catch((err) => {
            console.error('--billing group detail error', err)
        })

        if (!res || res.result !== 0) {
            return null
        }

        const group = new BillingGroup()
        group.fromJson(res.billing_group)

        this.setProperty('group', group)
        this.setProperty(
            'group_billings',
            res.billings || []
        )
        this.setProperty('currency', res.currency || {})
        return group
    }


    resetGroup() {
        this.group = new BillingGroup()
        this.group_billings = []
    }


    resetCreateOptions() {
        this.objects = []
        this.periods = []
        this.members = []
    }


    async getObjects(billing_type_id) {
        this.setProperty('objects', [])
        this.setProperty('periods', [])
        this.setProperty('members', [])

        if (!this.company_id || !billing_type_id) {
            return []
        }

        const res = await post(
            'manager/billing/objects',
            {
                company_id: this.company_id,
                billing_type_id
            }
        ).catch((err) => {
            console.error('--billing objects error', err)
        })

        const objects = res?.objects || []

        this.setProperty('objects', objects)

        return objects
    }


    async getTrainingPeriods(team_id) {
        this.setProperty('periods', [])
        this.setProperty('members', [])

        if (!this.company_id || !team_id) {
            return []
        }

        const res = await post(
            'manager/billing/training-periods',
            {
                company_id: this.company_id,
                team_id
            }
        ).catch((err) => {
            console.error('--billing periods error', err)
        })

        const periods = res?.periods || []

        this.setProperty('periods', periods)

        return periods
    }


    async getMembers({
        team_id = null,
        billing_group_id = null,
        search = ''
    } = {}) {
        if (!this.company_id) {
            return []
        }

        const res = await post(
            'manager/billing/members',
            {
                company_id: this.company_id,
                team_id,
                billing_group_id,
                search
            }
        ).catch((err) => {
            console.error('--billing members error', err)
        })

        const members = res?.members || []

        this.setProperty('members', members)

        return members
    }


    async addGroup(data) {
        const payload = {
            ...data,
            company_id: this.company_id,
            period_from: formatDate(data.period_from),
            period_to: formatDate(data.period_to)
        }

        return post(
            'manager/billing/group/add',
            payload
        ).catch((err) => {
            console.error('--billing group add error', err)
        })
    }


    async updateGroup(data) {
        const payload = {
            ...data,
            company_id: this.company_id,
            billing_group_id: data.billing_group_id || this.group.id
        }

        if (payload.period_from) {
            payload.period_from = formatDate(payload.period_from)
        }

        if (payload.period_to) {
            payload.period_to = formatDate(payload.period_to)
        }

        const res = await post(
            'manager/billing/group/update',
            payload
        ).catch((err) => {
            console.error('--billing group update error', err)
        })

        if (res?.billing_group) {
            this.group.fromJson(res.billing_group)
        }

        return res
    }


    async addMembers(company_user_ids, billing_group_id = null) {
        const groupId = billing_group_id || this.group.id

        if (!groupId || !company_user_ids?.length) {
            return null
        }

        const res = await post(
            'manager/billing/group/add-members',
            {
                company_id: this.company_id,
                billing_group_id: groupId,
                company_user_ids
            }
        ).catch((err) => {
            console.error('--billing add members error', err)
        })

        if (res?.result === 0) {
            await this.getGroup(groupId)
        }

        return res
    }


    async getTopDebts() {
        const res = await post(
            'manager/billing/top_debts',
            {
                company_id: this.company_id
            }
        ).catch((err) => {
            console.error('--billing debts error', err)
        })

        if (res?.result === 0) {
            this.setProperty(
                'top_debts',
                res.debts || []
            )
        }
    }


    async deleteBilling(billing_id) {
        const res = await post(
            'manager/billing/delete',
            {
                company_id: this.company_id,
                billing_id
            }
        ).catch((err) => {
            console.error('--billing delete error', err)
        })

        if (res?.result === 0) {
            if (this.group.id) {
                await this.getGroup(this.group.id)
            } else {
                await this.search()
            }
        }

        return res
    }


    async deleteCheque(billing_id, file_id) {
        const res = await post(
            'manager/billing/delete_cheque',
            {
                company_id: this.company_id,
                billing_id,
                file_id
            }
        ).catch((err) => {
            console.error('--cheque delete error', err)
        })

        return res
    }


    async generateGameBilling(game_id) {
        const res = await post(
            'manager/billing/generate_game',
            {
                company_id: this.company_id,
                game_id
            }
        ).catch((err) => {
            console.error('--generate game billing error', err)
        })

        if (res?.result === 0) {
            await this.search()
        }

        return res
    }


    async informGameBilling(game_id, billing_id) {
        return post(
            'manager/billing/inform_game',
            {
                company_id: this.company_id,
                game_id,
                billing_id
            }
        ).catch((err) => {
            console.error('--inform game billing error', err)
        })
    }

    async getMemberBillings({
        company_id,
        company_user_id,
        page_number = 1,
        page_size = 30,
        date_from = null,
        date_to = null,
        billing_type_id = 0,
        payment_method = "",
        is_paid = null,
        is_approved = null
    } = {}) {

        if (!company_id || !company_user_id) {
            return null;
        }

        this.setProperty("memberBillings", {
            ...this.memberBillings,
            items: [],
            total: 0,
            company_user: null,
            loading: true
        });

        try {

            const res = await post(
                "manager/billing/member/list",
                {
                    company_id,
                    company_user_id,
                    page_number,
                    page_size,
                    date_from,
                    date_to,
                    billing_type_id,
                    payment_method,
                    is_paid,
                    is_approved
                }
            );

            if (res?.result === 0) {

                this.setProperty("memberBillings", {
                    company_user: res.company_user || null,
                    items: res.items || [],
                    total: res.total || 0,
                    page_number: res.page_number || 1,
                    page_size: res.page_size || 30,
                    page_count: res.page_count || 0,
                    loading: false
                });

            } else {

                this.setProperty("memberBillings", {
                    ...this.memberBillings,
                    loading: false
                });

            }

            return res;

        } catch (error) {

            console.error("--member billings error", error);

            this.setProperty("memberBillings", {
                ...this.memberBillings,
                loading: false
            });

            return null;
        }
    }
}